import React, { useEffect, useState, useCallback, useRef } from "react";
import Webcam from "react-webcam";
import styled from "styled-components";
import Dimensions from "react-dimensions";
import { Icon, Button, Row, Col } from "antd";

import { XLg, Camera, ArrowRepeat, RecordCircle } from "react-bootstrap-icons";

const CameraButton = styled.button`
  width: 50px;
  height: 50px;
  background: none;
  margin: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: dashed 1px #ffffff;
  color: #ffffff;
  font-size: 20px;
`;

const VideoCamScreen = Dimensions({
  containerStyle: {
    width: "100vw",
    height: "100vh",
    position: "absolute",
    top: 0,
    left: 0,
  },
})((props) => {
  const [deviceIdx, setDeviceIdx] = useState(0);
  const [isPortrait, setIsPortrait] = useState(false);
  const [devices, setDevices] = useState([]);
  const [closeCamera, setCloseCamera] = useState(false);
  const webcamRef = useRef(null);
  const { className, children } = props;

  const { containerWidth, containerHeight } = props;

  const mediaRecorderRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);

  const handleStartCaptureClick = useCallback(() => {
    setCapturing(true);
    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
      mimeType: "video/mp4",
    });
    mediaRecorderRef.current.addEventListener(
      "dataavailable",
      handleDataAvailable
    );
    mediaRecorderRef.current.start();
  }, [webcamRef, setCapturing, mediaRecorderRef]);

  const handleDataAvailable = useCallback(
    ({ data }) => {
      console.log("data available:", data);
      if (data.size > 0) {
        setRecordedChunks((prev) => prev.concat(data));
      }
    },
    [setRecordedChunks]
  );

  const handleStopCaptureClick = useCallback(() => {
    mediaRecorderRef.current.stop();
    setCapturing(false);
  }, [mediaRecorderRef, webcamRef, setCapturing]);

  const handleDevices = useCallback(
    (mediaDevices) => {
      const cameraDevices = mediaDevices.filter(
        ({ kind }) => kind === "videoinput"
      );
      setDevices(cameraDevices);
      if (cameraDevices.length === 0) {
        setCloseCamera(true);
      }
    },
    [setDevices]
  );

  const handleUpload = () => {
    if (recordedChunks.length) {
      const blob = new Blob(recordedChunks, {
        type: "video/mp4",
      });
      const url = URL.createObjectURL(blob);
      if (props.onCapture) {
        props.onCapture(url);
      }
    }
  };

  useEffect(() => {
    /* Storing user's device details in a variable*/
    let details = window.navigator.userAgent;

    /* Creating a regular expression 
        containing some mobile devices keywords 
        to search it in details string*/
    let regexp = /android|iphone|kindle|ipad/i;
    let regexpTablet = /kindle|ipad/i;

    /* Using test() method to search regexp in details
        it returns boolean value*/
    let isMobileDevice = regexp.test(details);
    let isTabletDevice = regexpTablet.test(details);

    if (isMobileDevice) {
      if (isTabletDevice) {
        let wd = window.screen.width;
        let hg = window.screen.height;
        if (wd > hg) {
          setIsPortrait(() => false);
        } else {
          setIsPortrait(() => true);
        }
      } else {
        setIsPortrait(() => true);
      }
    } else {
      setIsPortrait(() => false);
    }
  }, []);

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(handleDevices);
  }, [handleDevices]);

  useEffect(() => {
    if (recordedChunks.length > 0 && !capturing) {
      handleUpload();
    }
  }, [recordedChunks]);

  return !closeCamera ? (
    <div className={className}>
      {devices.length > 0 && (
        <Webcam
          audio={true}
          ref={webcamRef}
          videoConstraints={{
            width: containerWidth,
            height: containerHeight,
            deviceId: devices[deviceIdx].deviceId,
          }}
        />
      )}
      <Row className={"buttons-row"}>
        <Col>
          <CameraButton
            onClick={() => {
              setCloseCamera(true);
              if (props.onClose) {
                props.onClose();
              }
            }}
          >
            <XLg />
          </CameraButton>
        </Col>
        <Col>
          {capturing ? (
            <CameraButton
              onClick={() => {
                handleStopCaptureClick();
              }}
            >
              <RecordCircle style={{ color: "red" }} />
            </CameraButton>
          ) : (
            <CameraButton
              onClick={() => {
                handleStartCaptureClick();
              }}
            >
              <Camera />
            </CameraButton>
          )}
        </Col>
        {devices.length > 1 && (
          <Col>
            <CameraButton
              onClick={() => {
                setDeviceIdx(
                  deviceIdx + 1 === devices.length ? 0 : deviceIdx + 1
                );
              }}
            >
              <ArrowRepeat />
            </CameraButton>
          </Col>
        )}
      </Row>
      {children}
    </div>
  ) : null;
});

const DesktopVideoCamera = styled(VideoCamScreen)`
  & {
    background: black;
    position: fixed;
    height: 100vh;
    width: 100vw;
    top: 0;
    left: 0;
    z-index: 999999;
    video {
      width: 100%;
      height: unset;
      @media only screen and (orientation: portrait) {
        position: absolute;
        height: 100vh;
        width: 177.77777778vh; /* 100 * 16 / 9 */
        min-width: 100%;
        min-height: 56.25vw;
        left: 50%; /* % of surrounding element */
        top: 50%;
        transform: translate(-50%, -50%);
      }
    }
    .buttons-row {
      height: 100%;
      position: absolute;
      @media only screen and (orientation: landscape) {
        right: 0;
        top: 0;
      }
      flex: 1;
      flex-direction: column;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: rgba(0, 0, 0, 0.2);
       {
        button {
          width: 100px;
          height: 100px;
          background: none;
          margin: 10px;
          i {
            color: white;
            font-size: 36px;
            margin-top: 4px;
          }
        }
      }
      @media only screen and (orientation: portrait) {
        height: 20vh;
        width: 100%;
        flex-direction: row;
        bottom: 0 !important;
      }
    }
  }
`;

export default DesktopVideoCamera;
